import type React from 'react';
import CheckIcon from '@/icons/check.svg';
import LockIcon from '@/icons/lock.svg';
import UsersIcon from '@/icons/users.svg';
import FrameIcon from '@/icons/frame.svg';
import PlusIcon from '@/icons/plus.svg';
import EditIcon from '@/icons/edit.svg';
import BoltIcon from '@/icons/bolt.svg';
import BoltPaywallIcon from '@/icons/bolt-paywall.svg';
import BillingIcon from '@/icons/billing.svg';
import SupportIcon from '@/icons/support.svg';
import FigmaIcon from '@/icons/figma-xs.svg';
import FigmaPaywallIcon from '@/icons/figma-paywall.svg';
import CodeIcon from '@/icons/code-paywall.svg';
import type {SubscriptionStatus} from './subscriptionService';
import {
  PLUS_MONTHLY_BASE,
  PLUS_DAILY_REFILL,
  PRO_MONTHLY_BASE,
  PRO_DAILY_REFILL,
} from './constants';

export interface PlanFeature {
  text: string;
  icon: React.FC<{className: string}>;
  available: boolean;
  tooltip?: string;
}

const creditsTooltip = (cap: number) =>
  `~1 credit per edit, ~2–3 per new screen,\ncapped at ${cap} credits per generation`;
const dailyRefillTooltip = (cap: number) =>
  `Daily top-ups on top of your monthly\ncredits, refilling until your balance\nreaches ${cap}`;
const FIGMA_TOOLTIP = 'Copy-paste designs\ndirectly to Figma';

// Free tier features
export const freeFeatures = ({
  freeInitialCredits,
  maxCreditsPerGeneration,
}: SubscriptionStatus['settings']) =>
  [
    {
      text: `Start with ${freeInitialCredits} credits`,
      icon: PlusIcon,
      available: true,
      tooltip: creditsTooltip(maxCreditsPerGeneration),
    },
    {
      text: 'Private projects',
      icon: LockIcon,
      available: true,
      tooltip: 'Your projects are only\nvisible to you',
    },
    {
      text: 'Limited edits',
      icon: CheckIcon,
      available: true,
      tooltip: 'Basic editing capabilities\nfor your designs',
    },
  ] satisfies PlanFeature[];

// Shared Plus-tier features (individual and team)
const corePlusFeatures = (maxCreditsPerGeneration: number): PlanFeature[] => [
  {
    text: `${PLUS_MONTHLY_BASE} monthly credits`,
    icon: PlusIcon,
    available: true,
    tooltip: creditsTooltip(maxCreditsPerGeneration),
  },
  {
    text: `${PLUS_DAILY_REFILL} daily credits`,
    icon: PlusIcon,
    available: true,
    tooltip: dailyRefillTooltip(PLUS_MONTHLY_BASE),
  },
  {
    text: 'Unlimited Figma exports',
    icon: FigmaPaywallIcon,
    available: true,
    tooltip: FIGMA_TOOLTIP,
  },
  {
    text: '3x generation speed',
    icon: BoltPaywallIcon,
    available: true,
    tooltip: 'Faster AI generations\ncompared to free plan',
  },
  {
    text: 'Code export',
    icon: CodeIcon,
    available: true,
    tooltip: 'Export your designs\nas code',
  },
];

export const plusFeatures = (
  maxCreditsPerGeneration: number,
): PlanFeature[] => [
  ...corePlusFeatures(maxCreditsPerGeneration),
  {
    text: 'Cancel anytime',
    icon: CheckIcon,
    available: true,
    tooltip: 'No long-term commitment,\ncancel whenever you want',
  },
];

export const teamPlusFeatures = (
  maxCreditsPerGeneration: number,
): PlanFeature[] => [
  ...corePlusFeatures(maxCreditsPerGeneration),
  {
    text: 'Centralized billing',
    icon: BillingIcon,
    available: true,
    tooltip: 'One bill for\nyour entire team',
  },
  {
    text: 'Shared projects',
    icon: UsersIcon,
    available: true,
    tooltip: 'See projects your\nteammates work on',
  },
];

// Shared Pro-tier features (individual and team). `everythingFrom` is the
// "Everything from [Team] Plus" row, placed right after the daily credits item.
const coreProFeatures = (
  maxCreditsPerGeneration: number,
  everythingFrom: PlanFeature,
): PlanFeature[] => [
  {
    text: `${PRO_MONTHLY_BASE} monthly credits`,
    icon: PlusIcon,
    available: true,
    tooltip: creditsTooltip(maxCreditsPerGeneration),
  },
  {
    text: `${PRO_DAILY_REFILL} daily credits`,
    icon: PlusIcon,
    available: true,
    tooltip: dailyRefillTooltip(PRO_MONTHLY_BASE),
  },
  everythingFrom,
  {
    text: 'Highest generation speed',
    icon: BoltPaywallIcon,
    available: true,
    tooltip: 'Fastest AI generation\nspeed available',
  },
  {
    text: 'Priority support',
    icon: SupportIcon,
    available: true,
    tooltip: 'Get help directly\nfrom the Banani team',
  },
];

export const proFeatures = (maxCreditsPerGeneration: number): PlanFeature[] =>
  coreProFeatures(maxCreditsPerGeneration, {
    text: 'Everything from Plus',
    icon: CheckIcon,
    available: true,
    tooltip: 'Includes all Plus\nfeatures and more',
  });

export const proTeamFeatures = (
  maxCreditsPerGeneration: number,
): PlanFeature[] =>
  coreProFeatures(maxCreditsPerGeneration, {
    text: 'Everything from Team Plus',
    icon: CheckIcon,
    available: true,
    tooltip: 'Includes all Team Plus\nfeatures and more',
  });

export const upgradeToTeamFeatures = [
  {text: 'Invite & manage members', icon: UsersIcon, available: true},
  {text: 'Centralised billing', icon: BillingIcon, available: true},
  {text: 'Unlimited designs', icon: FrameIcon, available: true},
  {text: 'Monthly + daily AI credits', icon: PlusIcon, available: true},
  {text: 'AI edits & generations', icon: EditIcon, available: true},
  {text: '2-3x faster generations', icon: BoltIcon, available: true},
  {text: 'Figma export', icon: FigmaIcon, available: true},
  {text: 'Code export', icon: CodeIcon, available: true},
  {text: 'Personal support', icon: SupportIcon, available: true},
  {text: 'Shared projects', icon: UsersIcon, available: true},
];

export const upgradeToYearlyFeatures = [
  {text: 'Save $141 per year', icon: CheckIcon, available: true},
  {text: 'Monthly + daily AI credits', icon: PlusIcon, available: true},
  {text: 'AI edits & generations', icon: EditIcon, available: true},
  {text: 'Unlimited designs', icon: FrameIcon, available: true},
  {text: '2-3x faster generations', icon: BoltIcon, available: true},
  {text: 'Figma export', icon: FigmaIcon, available: true},
  {text: 'Code export', icon: CodeIcon, available: true},
];
